/**
 * Pure helper to build job detail URLs for the static SRP shell.
 * No hooks or context; used so static shell can render job links without SearchProvider.
 */
import { generateJobUrl, toRelativePath } from './generate-url';
import type { Locale } from './generate-url';
import { BASE_URL } from '../constants/url-constants';
import { getDevSiteOrigin } from '../constants/dev-site-origin';
import {
  resolveJobUrlCompany,
  resolveJobUrlLocation,
  resolveJobUrlRemoteness,
  resolveJobUrlTitle,
} from './job-url-input';

export interface StaticJobUrlParams {
  hit: {
    objectID: string;
    normalisedTitle?: string;
    title?: string;
    companyId?: string;
    location?: string | string[];
    remoteness?: string;
  };
  locale: Locale;
  currentPathname: string;
  query: string;
  filters: Record<string, string[]> | string;
  page: number;
  indexName: string;
  baseUrl?: string;
  /** SSR: page request URL so dev job links use the correct port */
  devSiteRequestUrl?: string | null;
}

/**
 * Returns a clean relative job URL for the static SRP (no tracking params).
 */
export function buildStaticJobUrl(params: StaticJobUrlParams): string {
  // ApiHandler Lambda bundle has import.meta but no Vite-injected import.meta.env — use optional chaining.
  const baseUrl =
    params.baseUrl ??
    (import.meta.env?.DEV ? getDevSiteOrigin({ requestUrl: params.devSiteRequestUrl }) : BASE_URL);
  const absolute = generateJobUrl({
    baseUrl,
    jobId: params.hit.objectID,
    locale: params.locale,
    title: resolveJobUrlTitle(params.hit),
    company: resolveJobUrlCompany(params.hit),
    location: resolveJobUrlLocation(params.hit),
    remoteness: resolveJobUrlRemoteness(params.hit),
  });

  return toRelativePath(absolute);
}
